import {
  FINANCE_EMPLOYMENT_STATUS,
  FINANCE_FAMILY_STATUS,
  FINANCE_RESIDENCY_STATUS,
  FINANCE_RESIDENTIAL_STATUS,
} from '@/data/FinanceApi/FinanceApi.types';

/**
 * Buy type enums and constants
 */
export const enum BUY_TYPE {
  PERSONAL = 'personal',
  BUSINESS = 'business',
}

export const BUY_TYPES = ['personal', 'business'] as const;
export type BuyTypes = (typeof BUY_TYPES)[number];

/**
 * Payment options enums and constants
 */
export const enum PAYMENT_OPTIONS_TYPE {
  INSTANT_FINANCE_QUOTE = 'instantFinanceQuote',
  CARMA_CONTACT = 'carmaContact',
  MY_OWN_FUND = 'myOwnFund',
}

/**
 * Payment options as a const object (for use with Zod schemas)
 */
export const PAYMENT_OPTIONS = {
  INSTANT_FINANCE_QUOTE: 'instantFinanceQuote',
  CARMA_CONTACT: 'carmaContact',
  MY_OWN_FUND: 'myOwnFund',
} as const;

export const enum PAYMENT_OPTIONS_SUMMARY_TYPE {
  INSTANT_FINANCE_QUOTE = 'instantFinanceQuote',
  CARMA_CONTACT = 'carmaContact',
  MY_OWN_FUND = 'myOwnFund',
  PAY_IN_FULL = 'payInFull',
}

/**
 * Trade-in options
 */
export const enum TRADE_IN_TYPE {
  TRADE_IN = 'yes',
  NO_TRADE_IN = 'no',
}

/**
 * Finance usage options
 */
export const enum USE_FINANCE_TYPE {
  FINANCE = 'yes',
  NO_FINANCE = 'no',
}

/**
 * Delivery options
 */
export const enum DELIVERY_OPTION {
  DELIVERY = 'delivery',
  COLLECTION = 'collection',
}

export const DELIVERY_OPTIONS = ['delivery', 'collection'] as const;

/**
 * Payment methods
 */
export enum PAYMENT_METHODS_TYPE {
  RESERVATION = 'pay500Reservation',
  FULL_AMOUNT = 'payFullAmount',
}

/**
 * Terms and conditions
 */
export const enum TERMS_AND_CONDITIONS {
  CHECKED = 'checked',
}

/**
 * Finance quote radio options
 */
export const enum FINANCE_QUOTE_RADIO_TYPE {
  YES_OPTION = 'yes',
  NO_OPTION = 'no',
}

/**
 * Finance quote repayment frequency
 */
export const enum FINANCE_QUOTE_REPAYMENT_FREQUENCY {
  MONTHLY = 'monthly',
  WEEKLY = 'weekly',
}

/**
 * YouX sending status
 */
export const enum SEND_YOUX_STATUS {
  UNSENT = 'unsent',
  LOADING = 'loading',
  SENT = 'sent',
}

/**
 * Extended coverage options
 */
export const EXTENDED_COVERAGE_OPTIONS = [
  'extended-coverage-1',
  'extended-coverage-3',
  'extended-coverage-5',
  'extended-coverage-skip',
] as const;
export type ExtendedCoverageOptions = (typeof EXTENDED_COVERAGE_OPTIONS)[number];

/**
 * Default pickup address
 */
export const DEFAULT_PICKUP_ADDRESS = {
  street: '1-3 Burrows Road',
  apartment: 'UNIT 7',
  suburb: 'St Peters',
  postcode: '2044',
  state: 'NSW',
};

/**
 * BYO Finance lender options
 */
export const BYOF_LENDER_OPTIONS = [
  { label: 'Carma finance', value: 'carmaFinance' },
  { label: 'Angle Finance', value: 'angleFinance' },
  { label: 'ANZ', value: 'ANZ' },
  { label: 'CBA', value: 'CBA' },
  { label: 'Finance One', value: 'financeOne' },
  { label: 'Latitude Financial Services', value: 'latitudeFinancialServices' },
  { label: 'Liberty Finance', value: 'libertyFinance' },
  { label: 'Macquarie', value: 'macquarie' },
  { label: 'Money3', value: 'money3' },
  { label: 'NAB', value: 'NAB' },
  { label: 'Pepper Money', value: 'pepperMoney' },
  { label: 'Plenti', value: 'plenti' },
  { label: 'St.George', value: 'st.George' },
  { label: 'Stratton Finance', value: 'strattonFinance' },
  { label: 'Westpac', value: 'westpac' },
  { label: 'Wisr', value: 'wisr' },
  { label: 'Other', value: 'other' },
] as const;

export const CHECKOUT_DATA_LOCAL = 'checkout-data-local';

export const familyStatusOptions = [
  {
    value: FINANCE_FAMILY_STATUS.SINGLE,
    label: 'Single',
  },
  {
    value: FINANCE_FAMILY_STATUS.MARRIED,
    label: 'Married',
  },
  {
    value: FINANCE_FAMILY_STATUS.DE_FACTO,
    label: 'De Facto',
  },
  {
    value: FINANCE_FAMILY_STATUS.WIDOWED,
    label: 'Widowed',
  },
  {
    value: FINANCE_FAMILY_STATUS.SEPARATED,
    label: 'Separated',
  },
  {
    value: FINANCE_FAMILY_STATUS.DIVORCED,
    label: 'Divorced',
  },
  {
    value: FINANCE_FAMILY_STATUS.OTHER,
    label: 'Other',
  },
];

export const residentialStatusOptions = [
  {
    value: FINANCE_RESIDENTIAL_STATUS.HOMEOWNER,
    label: "I'm a homeowner",
  },
  {
    value: FINANCE_RESIDENTIAL_STATUS.RENTING,
    label: "I'm renting",
  },
  {
    value: FINANCE_RESIDENTIAL_STATUS.BOARDER,
    label: "I'm a boarder",
  },
  {
    value: FINANCE_RESIDENTIAL_STATUS.LIVING_WITH_PARENTS,
    label: "I'm living with parents",
  },
  {
    value: FINANCE_RESIDENTIAL_STATUS.EMPLOYER_SUPPLIED_ACCOMMODATION,
    label: 'Employer supplied accommodation',
  },
];

export const employmentStatusOptions = [
  {
    value: FINANCE_EMPLOYMENT_STATUS.FULL_TIME,
    label: 'Full time',
  },
  {
    value: FINANCE_EMPLOYMENT_STATUS.PART_TIME,
    label: 'Part time',
  },
  {
    value: FINANCE_EMPLOYMENT_STATUS.CASUAL,
    label: 'Casual',
  },
  {
    value: FINANCE_EMPLOYMENT_STATUS.CONTRACT,
    label: 'Contract',
  },
  {
    value: FINANCE_EMPLOYMENT_STATUS.SELF_EMPLOYED,
    label: 'Self-employed',
  },
  {
    value: FINANCE_EMPLOYMENT_STATUS.RETIRED,
    label: 'Retired',
  },
  {
    value: FINANCE_EMPLOYMENT_STATUS.UNEMPLOYED,
    label: 'Unemployed',
  },
];

export const residencyStatusOptions = [
  {
    value: FINANCE_RESIDENCY_STATUS.AUSTRALIAN_CITIZEN,
    label: 'Australian citizen',
  },
  {
    value: FINANCE_RESIDENCY_STATUS.PERMANENT_RESIDENT,
    label: 'Permanent resident',
  },
  {
    value: FINANCE_RESIDENCY_STATUS.TEMPORARY_RESIDENT,
    label: 'Temporary resident',
  },
];

export const financeQuoteInfoRadioTypes = [
  {
    value: FINANCE_QUOTE_RADIO_TYPE.YES_OPTION,
    label: 'Yes',
  },
  {
    value: FINANCE_QUOTE_RADIO_TYPE.NO_OPTION,
    label: 'No',
  },
];

export const repaymentFrequencyOptions = [
  {
    label: 'Weekly',
    value: FINANCE_QUOTE_REPAYMENT_FREQUENCY.WEEKLY,
  },
  {
    label: 'Monthly',
    value: FINANCE_QUOTE_REPAYMENT_FREQUENCY.MONTHLY,
  },
];
